import React, { useCallback, useState } from "react";
import * as E from "fp-ts/lib/Either";
import type { Ok } from "@ps-bridge/ok";
import { ok } from "@ps-bridge/ok";
import type { OptionTs } from "@ps-bridge/interop";
import { buildFeedbackSubmissionViewProps as statementClassifierProgramMarker } from "@ps-bridge/feedbackSubmission.program";
import { useStatementClassifierClient } from "@contexts/StatementClassifierClient.context";
import {
  decodeStatementClassifierGuidance,
  type StatementClassifierJobId,
  type StatementClassifierJobStatus,
  type StatementClassifierPreviewRow,
  type StatementClassifierWorkbookFileName,
} from "@services/clients/StatementClassifierClient";
import { evaluateTaskEither } from "@utils/effects/taskEitherRunner";
import * as Option from "@utils/Option";
import StatementClassifierPagePanel, {
  type StatementClassifierPhase,
} from "@view/panels/statement-classifier/StatementClassifierPage.panel";

const defaultGuidance =
  "Classify statement rows into clear business expense categories. Keep the preview concise and flag uncertain rows with lower confidence.";
const defaultBusinessDescription =
  "Describe the business activity, clients, revenue model, and recurring vendors for this statement run.";
const defaultBusinessExamples =
  "List vendors or transaction patterns that should usually count as business expenses.";
const defaultPersonalExamples =
  "List vendors or transaction patterns that should usually count as personal expenses.";
const defaultAmbiguousRules =
  "Explain how to handle ambiguous rows, transfers, refunds, reimbursements, and unknown vendors.";
const defaultExtraNotes =
  "Add any statement-specific context that should affect this classification run.";
const defaultClassificationAdjustments =
  "List contextual rules, such as when meals, travel, software, or transfers should be treated as business or personal.";
const chooseMessage = "Choose one or more statement files and adjust the guidance before submitting.";
const statementClassifierProgramName = statementClassifierProgramMarker.name;
const statusPollDelayMs = 1_000;
const statusPollAttempts = 180;

const downloadBlob = (blob: Blob, filename: StatementClassifierWorkbookFileName): Ok => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
  return ok;
};

const requestFailureMessage =
  "Statement Classifier could not finish the current request. Try again or contact support if it continues.";

const delay = (millis: number): Promise<Ok> =>
  new Promise((resolve) => {
    window.setTimeout(() => resolve(ok), millis);
  });

const failureMessage = (_status: StatementClassifierJobStatus): string =>
  "Statement classification failed before preview rows were ready.";

const statusHasWorkbook = (status: StatementClassifierJobStatus): boolean =>
  Option.fold(status.workbook, () => false, () => true);

export type StatementClassifierPollingOverrides = {
  readonly statusPollDelayMs: number;
  readonly statusPollAttempts: number;
};

export type StatementClassifierPageProps = {
  readonly featureEnabled: boolean;
  readonly pollingOverrides: OptionTs<StatementClassifierPollingOverrides>;
};

const StatementClassifierPage: React.FC<StatementClassifierPageProps> = ({
  featureEnabled,
  pollingOverrides,
}) => {
  const client = useStatementClassifierClient();
  const polling = Option.fold(
    pollingOverrides,
    () => ({
      statusPollDelayMs,
      statusPollAttempts,
    }),
    (value) => value,
  );
  const [selectedFiles, setSelectedFiles] = useState<readonly File[]>([]);
  const [guidance, setGuidance] = useState(defaultGuidance);
  const [businessDescription, setBusinessDescription] = useState(defaultBusinessDescription);
  const [businessExamples, setBusinessExamples] = useState(defaultBusinessExamples);
  const [personalExamples, setPersonalExamples] = useState(defaultPersonalExamples);
  const [ambiguousRules, setAmbiguousRules] = useState(defaultAmbiguousRules);
  const [extraNotes, setExtraNotes] = useState(defaultExtraNotes);
  const [classificationAdjustments, setClassificationAdjustments] = useState(defaultClassificationAdjustments);
  const [phase, setPhase] = useState<StatementClassifierPhase>("idle");
  const [message, setMessage] = useState(chooseMessage);
  const [jobId, setJobId] = useState<OptionTs<StatementClassifierJobId>>(Option.none<StatementClassifierJobId>());
  const [status, setStatus] = useState<OptionTs<StatementClassifierJobStatus>>(Option.none<StatementClassifierJobStatus>());
  const [previewRows, setPreviewRows] = useState<readonly StatementClassifierPreviewRow[]>([]);
  const workbookReady = Option.fold(status, () => false, statusHasWorkbook);

  const onFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): Ok => {
    const inputFiles = event.target.files;
    const nextFiles = inputFiles == undefined ? [] : Array.from(inputFiles);
    setSelectedFiles(nextFiles);
    setPreviewRows([]);
    setStatus(Option.none<StatementClassifierJobStatus>());
    setJobId(Option.none<StatementClassifierJobId>());
    if (nextFiles.length === 0) {
      setPhase("idle");
      setMessage(chooseMessage);
      return ok;
    }
    setPhase("ready");
    setMessage(`${nextFiles.length} file${nextFiles.length === 1 ? "" : "s"} ready for classification.`);
    return ok;
  }, []);

  const onGuidanceChange = useCallback((value: string): Ok => {
    setGuidance(value);
    return ok;
  }, []);

  const onBusinessDescriptionChange = useCallback((value: string): Ok => {
    setBusinessDescription(value);
    return ok;
  }, []);

  const onBusinessExamplesChange = useCallback((value: string): Ok => {
    setBusinessExamples(value);
    return ok;
  }, []);

  const onPersonalExamplesChange = useCallback((value: string): Ok => {
    setPersonalExamples(value);
    return ok;
  }, []);

  const onAmbiguousRulesChange = useCallback((value: string): Ok => {
    setAmbiguousRules(value);
    return ok;
  }, []);

  const onExtraNotesChange = useCallback((value: string): Ok => {
    setExtraNotes(value);
    return ok;
  }, []);

  const onClassificationAdjustmentsChange = useCallback((value: string): Ok => {
    setClassificationAdjustments(value);
    return ok;
  }, []);

  const onSubmit = useCallback(async (): Promise<Ok> => {
    if (!featureEnabled || selectedFiles.length === 0 || phase === "submitting") {
      return ok;
    }

    const guidanceResult = decodeStatementClassifierGuidance({
      text: guidance,
      businessDescription,
      businessExamples,
      personalExamples,
      ambiguousRules,
      extraNotes,
      classificationAdjustments,
      sections: JSON.parse("null"),
    });
    if (guidanceResult.type === "error") {
      setPhase("error");
      setMessage("Guidance is required before classification can run.");
      return ok;
    }

    setPhase("submitting");
    setMessage("Submitting the statement job and loading preview rows...");
    const submitResult = await evaluateTaskEither(client.submitJob([...selectedFiles], guidanceResult.value));
    if (E.isLeft(submitResult)) {
      setPhase("error");
      setMessage(requestFailureMessage);
      return ok;
    }

    setJobId(Option.some(submitResult.right.jobId));
    setStatus(Option.some(submitResult.right.status));
    let currentStatus = submitResult.right.status;
    for (let attempt = 0; attempt < polling.statusPollAttempts && currentStatus.phase !== "Completed"; attempt += 1) {
      if (currentStatus.phase === "Failed") {
        setPhase("error");
        setMessage(failureMessage(currentStatus));
        return ok;
      }

      await delay(polling.statusPollDelayMs);
      const statusResult = await evaluateTaskEither(client.getJobStatus(submitResult.right.jobId));
      if (E.isLeft(statusResult)) {
        setPhase("processingDelayed");
        setMessage("Could not refresh job status. You can check again without resubmitting the files.");
        return ok;
      }
      currentStatus = statusResult.right.status;
      setStatus(Option.some(currentStatus));
    }

    if (currentStatus.phase !== "Completed") {
      setPhase("processingDelayed");
      setMessage("This job is still running. You can check again without resubmitting the files.");
      return ok;
    }

    setPhase("workbookReadyPreviewLoading");
    setMessage("Workbook is ready. Loading preview rows...");

    const previewResult = await evaluateTaskEither(client.getPreview(submitResult.right.jobId));
    if (E.isLeft(previewResult)) {
      setPhase("previewUnavailable");
      setMessage("Workbook is ready, but preview rows could not be loaded. You can download the workbook now or retry preview.");
      return ok;
    }

    setPreviewRows(previewResult.right.rows);
    setPhase("previewReady");
    setMessage("Preview rows are ready. Review them before downloading the workbook.");
    return ok;
  }, [
    ambiguousRules,
    businessDescription,
    businessExamples,
    classificationAdjustments,
    client,
    extraNotes,
    featureEnabled,
    guidance,
    personalExamples,
    polling.statusPollAttempts,
    polling.statusPollDelayMs,
    phase,
    selectedFiles,
  ]);

  const onCheckStatus = useCallback(async (): Promise<Ok> => {
    if (!featureEnabled || phase === "submitting" || phase === "downloading") {
      return ok;
    }

    return Option.fold(
      jobId,
      async () => {
        setPhase("error");
        setMessage("Run classification before checking job status.");
        return ok;
      },
      async (value) => {
        setPhase("submitting");
        setMessage("Checking the latest job status...");
        const statusResult = await evaluateTaskEither(client.getJobStatus(value));
        if (E.isLeft(statusResult)) {
          setPhase("processingDelayed");
          setMessage("Could not refresh job status. You can check again without resubmitting the files.");
          return ok;
        }

        const nextStatus = statusResult.right.status;
        setStatus(Option.some(nextStatus));
        if (nextStatus.phase === "Failed") {
          setPhase("error");
          setMessage(failureMessage(nextStatus));
          return ok;
        }
        if (nextStatus.phase !== "Completed") {
          setPhase("processingDelayed");
          setMessage("This job is still running. You can check again without resubmitting the files.");
          return ok;
        }

        setPhase("workbookReadyPreviewLoading");
        setMessage("Workbook is ready. Loading preview rows...");
        const previewResult = await evaluateTaskEither(client.getPreview(value));
        if (E.isLeft(previewResult)) {
          setPhase("previewUnavailable");
          setMessage("Workbook is ready, but preview rows could not be loaded. You can download the workbook now or retry preview.");
          return ok;
        }

        setPreviewRows(previewResult.right.rows);
        setPhase("previewReady");
        setMessage("Preview rows are ready. Review them before downloading the workbook.");
        return ok;
      },
    );
  }, [client, featureEnabled, jobId, phase]);

  const onRetryPreview = useCallback(async (): Promise<Ok> => {
    if (!featureEnabled || phase === "submitting" || phase === "downloading") {
      return ok;
    }

    return Option.fold(
      jobId,
      async () => {
        setPhase("error");
        setMessage("Run classification before retrying preview rows.");
        return ok;
      },
      async (value) => {
        setPhase("workbookReadyPreviewLoading");
        setMessage("Retrying preview rows...");
        const previewResult = await evaluateTaskEither(client.getPreview(value));
        if (E.isLeft(previewResult)) {
          setPhase("previewUnavailable");
          setMessage("Workbook is ready, but preview rows could not be loaded. You can download the workbook now or retry preview.");
          return ok;
        }

        setPreviewRows(previewResult.right.rows);
        setPhase("previewReady");
        setMessage("Preview rows are ready. Review them before downloading the workbook.");
        return ok;
      },
    );
  }, [client, featureEnabled, jobId, phase]);

  const onDownload = useCallback(async (): Promise<Ok> => {
    if (!featureEnabled || phase === "downloading") {
      return ok;
    }

    const previousPhase = phase;
    return Option.fold(
      jobId,
      async () => {
        setPhase("error");
        setMessage("Run classification before downloading the workbook.");
        return ok;
      },
      async (value) => {
        setPhase("downloading");
        setMessage("Preparing the workbook download...");
        const workbookResult = await evaluateTaskEither(client.downloadWorkbook(value));
        if (E.isLeft(workbookResult)) {
          if (workbookReady) {
            setPhase(previousPhase);
            setMessage("Workbook is ready, but the download could not be prepared. Try downloading again.");
            return ok;
          }
          setPhase("error");
          setMessage(requestFailureMessage);
          return ok;
        }
        downloadBlob(workbookResult.right.content, workbookResult.right.fileName);
        setPhase("downloaded");
        setMessage("Workbook downloaded.");
        return ok;
      },
    );
  }, [client, featureEnabled, jobId, phase, workbookReady]);

  return (
    <StatementClassifierPagePanel
      featureEnabled={featureEnabled}
      programName={statementClassifierProgramName}
      phase={featureEnabled ? phase : "featureUnavailable"}
      selectedFiles={selectedFiles}
      guidance={guidance}
      businessDescription={businessDescription}
      businessExamples={businessExamples}
      personalExamples={personalExamples}
      ambiguousRules={ambiguousRules}
      extraNotes={extraNotes}
      classificationAdjustments={classificationAdjustments}
      message={message}
      status={status}
      previewRows={previewRows}
      workbookReady={workbookReady}
      onFileChange={onFileChange}
      onGuidanceChange={onGuidanceChange}
      onBusinessDescriptionChange={onBusinessDescriptionChange}
      onBusinessExamplesChange={onBusinessExamplesChange}
      onPersonalExamplesChange={onPersonalExamplesChange}
      onAmbiguousRulesChange={onAmbiguousRulesChange}
      onExtraNotesChange={onExtraNotesChange}
      onClassificationAdjustmentsChange={onClassificationAdjustmentsChange}
      onSubmit={onSubmit}
      onCheckStatus={onCheckStatus}
      onRetryPreview={onRetryPreview}
      onDownload={onDownload}
    />
  );
};

export default StatementClassifierPage;
